import tierStandardIcon from 'dashboard/assets/images/pricing/tier-standard.png';
import tierSignatureIcon from 'dashboard/assets/images/pricing/tier-signature.png';
import tierPrestigeIcon from 'dashboard/assets/images/pricing/tier-prestige.png';
import tierIconicIcon from 'dashboard/assets/images/pricing/tier-iconic.png';

export const SPACE_TIERS = ['standard', 'signature', 'prestige', 'iconic'];

/** @deprecated Use SPACE_TIERS for space selection; rate still accepts none */
export const TIERS = ['none', ...SPACE_TIERS];

export const TIER_COLORS = {
  standard: '#746860',
  signature: '#AF8B70',
  prestige: '#A77A3E',
  iconic: '#CE9E5D',
};

export const TIER_ICONS = {
  standard: tierStandardIcon,
  signature: tierSignatureIcon,
  prestige: tierPrestigeIcon,
  iconic: tierIconicIcon,
};

export const TIER_BG_CLASSES = {
  none: '',
  standard: '',
  signature: '',
  prestige: '',
  iconic: '',
};

export const SUGGESTABLE_UNITS = ['per_hour', 'per_day'];

export const TIER_TO_BAND = {
  standard: 'standard',
  signature: 'signature',
  prestige: 'prestige',
  iconic: 'iconic',
  // Legacy aliases
  bronze: 'standard',
  silver: 'signature',
  gold: 'prestige',
  platinum: 'iconic',
  low: 'standard',
  mid: 'signature',
  high: 'prestige',
  luxury: 'iconic',
};

export const LEGACY_TIER_MAP = {
  bronze: 'standard',
  silver: 'signature',
  gold: 'prestige',
  platinum: 'iconic',
};

export const SUGGESTION_TOLERANCE = 0.05;
export const DEFAULT_DAY_HOURS = 10.5;

export const normalizeTier = tier => {
  const value = (tier || 'none').toLowerCase();
  return LEGACY_TIER_MAP[value] || value;
};

export const tierBgClass = tier => TIER_BG_CLASSES[normalizeTier(tier)] || '';

export const tierColor = tier => TIER_COLORS[normalizeTier(tier)] || null;

export const tierBgStyle = tier => {
  const color = tierColor(tier);
  if (!color) return null;
  return { backgroundColor: `${color}40` };
};

export const tierIcon = tier => TIER_ICONS[normalizeTier(tier)] || null;

export const isSuggestableUnit = unit => SUGGESTABLE_UNITS.includes(unit);

export const normalizeSuggestionBands = bands => {
  let list = bands;
  if (typeof list === 'string') {
    try {
      list = JSON.parse(list);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map(band => ({
      label: String(band?.label || band?.Label || '').toLowerCase(),
      metal: String(band?.metal || band?.Metal || '').toLowerCase(),
      point: Number(band?.point ?? band?.Point),
    }))
    .filter(band => Number.isFinite(band.point));
};

/**
 * Resolve the hourly EUR reference point for a rate tier from stored Moneytron data.
 * `none` uses the CV mean (suggestedPrice); other tiers use the matching band point.
 */
export const referenceHourlyPoint = ({
  tier,
  suggestedPrice,
  bands,
} = {}) => {
  const normalizedTier = normalizeTier(tier || 'none');
  if (normalizedTier && normalizedTier !== 'none') {
    const bandLabel = TIER_TO_BAND[normalizedTier];
    const list = normalizeSuggestionBands(bands);
    const match = list.find(
      band =>
        (bandLabel && band.label === bandLabel) ||
        band.metal === normalizedTier ||
        band.label === normalizedTier ||
        TIER_TO_BAND[band.metal] === bandLabel ||
        TIER_TO_BAND[band.label] === bandLabel
    );
    return match ? match.point : null;
  }

  const point = Number(suggestedPrice);
  return Number.isFinite(point) ? point : null;
};

export const scaleSuggestionPoint = (hourlyPoint, unit, dayHours = DEFAULT_DAY_HOURS) => {
  if (hourlyPoint == null) return null;
  if (unit === 'per_hour') return hourlyPoint;
  if (unit === 'per_day') return hourlyPoint * (Number(dayHours) || DEFAULT_DAY_HOURS);
  return null;
};

/**
 * Compare a configured rate to its suggestion point (±5%).
 * Returns 'ok' | 'low' | 'high' | null.
 */
export const suggestionStatusForPrice = ({
  price,
  unit,
  currency,
  tier,
  suggestedPrice,
  suggestedCurrency = 'EUR',
  bands,
  dayHours = DEFAULT_DAY_HOURS,
} = {}) => {
  if (!isSuggestableUnit(unit)) return null;
  if ((currency || 'EUR').toUpperCase() !== (suggestedCurrency || 'EUR').toUpperCase()) {
    return null;
  }

  const hourly = referenceHourlyPoint({ tier, suggestedPrice, bands });
  const reference = scaleSuggestionPoint(hourly, unit, dayHours);
  const value = Number(price);
  if (reference == null || !Number.isFinite(value)) return null;

  const floor = reference * (1 - SUGGESTION_TOLERANCE);
  const ceiling = reference * (1 + SUGGESTION_TOLERANCE);
  if (value < floor) return 'low';
  if (value > ceiling) return 'high';
  return 'ok';
};

export const priceMatchesSuggestion = (price, suggestionPoint, tolerance = SUGGESTION_TOLERANCE) => {
  const value = Number(price);
  const reference = Number(suggestionPoint);
  if (!Number.isFinite(value) || !Number.isFinite(reference)) return false;
  const floor = reference * (1 - tolerance);
  const ceiling = reference * (1 + tolerance);
  return value >= floor && value <= ceiling;
};
